import React from 'react';
import { inject, observer, Provider } from 'mobx-react';
import FontAwesome from 'react-fontawesome';
import { makeObservable, observable, reaction, action } from 'mobx';
import { Button, Row } from 'react-bootstrap';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';
import HeatmapGroupTransition from './Transitions/HeatmapGroupTransition/HeatmapGroupTransition';
import LineTransition from './Transitions/LineTransition/LineTransition';
import SankeyTransition from './Transitions/SankeyTransition/SankeyTransition';
import HeatmapTimepoint from './Timepoints/Heatmap';
import GroupTimepoint from './Timepoints/GroupTimepoint';
import TimepointLabels from './PlotLabeling/TimepointLabels';
import VariableLabels from './VariableLabels/VariableLabels';
import RowOperators from './RowOperators/RowOperators';
import Legend from './Legend';
import { withUICallbacks } from './UICallbacksContext';

/**
 * Component for the Block view
 */
const BlockView = inject(
	'rootStore',
	'uiStore',
	'undoRedoStore'
)(
	observer(
		class BlockView extends React.Component {
			highlightedVariable = '';
			order = ['labels', 'variableLabels', 'controls', 'view', 'legend'];
			width = window.innerWidth;
			panes = {
				labels: { width: ((window.innerWidth - 40) / 10) * 0.5, active: false },
				variableLabels: { width: ((window.innerWidth - 40) / 10) * 1.5, active: false },
				controls: { width: ((window.innerWidth - 40) / 10) * 1.0, active: false },
				view: { width: ((window.innerWidth - 40) / 10) * 5.5, active: false },
				legend: { width: ((window.innerWidth - 40) / 10) * 1.5, active: false },
			};
			ref = React.createRef();
			active = {
				labels: false,
				variableLabels: false,
				controls: false,
				view: false,
				legend: false,
			};

			constructor(props) {
				super(props);
				this.padding = 20;
				this.blockView = React.createRef();

				makeObservable(this, {
					highlightedVariable: observable,
					order: observable,
					width: observable,
					panes: observable,
					active: observable,
					updateDimensions: action,
					updatePaneWidth: action,
					setHighlightedVariable: action,
					removeHighlightedVariable: action,
					handleTimeClick: action,
				});

				this.handleTimeClick = this.handleTimeClick.bind(this);
				this.setHighlightedVariable = this.setHighlightedVariable.bind(this);
				this.removeHighlightedVariable = this.removeHighlightedVariable.bind(this);
				this.updateDimensions = this.updateDimensions.bind(this);
				this.updatePaneWidth = this.updatePaneWidth.bind(this);

				reaction(
					() => this.panes.view.width,
					(width) => {
						this.props.rootStore.visStore.setPlotWidth(width - 10);
					}
				);
			}

			/**
			 * Add event listener
			 */
			componentDidMount() {
				this.width = this.ref.current.getBoundingClientRect().width;
				this.updateDimensions();

				this.props.rootStore.visStore.setPlotWidth(this.panes.view.width - 10);
				this.props.rootStore.visStore.setPlotHeight(
					window.innerHeight - this.blockView.current.getBoundingClientRect().top
				);
				window.addEventListener('resize', this.updateDimensions);
			}

			/**
			 * Remove event listener
			 */
			componentWillUnmount() {
				window.removeEventListener('resize', this.updateDimensions);
			}

			/**
			 * updates view dimensions
			 */
			updateDimensions() {
				const prevWidth = Object.values(this.panes)
					.map((d) => d.width)
					.reduce((a, b) => a + b);
				this.panes = {
					labels: {
						width: (this.width - 40) / (prevWidth / this.panes.labels.width),
					},
					variableLabels: {
						width: (this.width - 40) / (prevWidth / this.panes.variableLabels.width),
					},
					controls: {
						width: (this.width - 40) / (prevWidth / this.panes.controls.width),
					},
					view: {
						width: (this.width - 40) / (prevWidth / this.panes.view.width),
					},
					legend: {
						width: (this.width - 40) / (prevWidth / this.panes.legend.width),
					},
				};
				this.props.rootStore.visStore.setPlotHeight(
					window.innerHeight - this.blockView.current.getBoundingClientRect().top
				);
			}

			/**
			 * updates a single pane width
			 * @param {string} paneName
			 * @param {number} width
			 */
			updatePaneWidth(paneName, width) {
				this.panes[paneName].width = width;
			}

			/**
			 * sets a variable to be highlighted
			 * @param {string} newHighlighted
			 */
			setHighlightedVariable(newHighlighted) {
				this.highlightedVariable = newHighlighted;
			}

			/**
			 * removes the highlighted variable
			 */
			removeHighlightedVariable() {
				this.highlightedVariable = '';
			}

			/**
			 * handle visualizing real time
			 */
			handleTimeClick() {
				this.props.rootStore.dataStore.applyPatientOrderToAll(0);
				this.props.uiStore.setRealTime(!this.props.uiStore.realTime);
				this.props.undoRedoStore.saveRealTimeHistory(this.props.uiStore.realTime);
			}

			/**
			 * gets timepoints and transitions
			 * @return {*[]}
			 */
			getTimepointAndTransitions() {
				const timepoints = [];
				const transitions = [];
				this.props.rootStore.dataStore.timepoints.forEach((d, i) => {
					let rectWidth;
					// check the type of the timepoint to get the correct width of the heatmap rectangles
					if (d.type === 'between') {
						rectWidth = this.props.rootStore.visStore.sampleRectWidth / 2;
					} else {
						rectWidth = this.props.rootStore.visStore.sampleRectWidth;
					}
					// create timepoints
					if (d.heatmap) {
						if (d.isGrouped) {
							const transformTP = `translate(${this.props.rootStore.visStore.getTpXTransform(i)},${this.props.rootStore.visStore.timepointPositions.timepoint[i]})`;
							timepoints.push(
								<g key={d.globalIndex} transform={transformTP}>
									<Provider
										dataStore={this.props.rootStore.dataStore}
										visStore={this.props.rootStore.visStore}
									>
										<GroupTimepoint
											group={d.grouped}
											heatmap={d.heatmap}
											index={i}
											currentVariables={
												this.props.rootStore.dataStore.variableStores[d.type]
													.fullCurrentVariables
											}
											rectWidth={rectWidth}
											tooltipFunctions={this.props.tooltipFunctions}
											primaryVariableId={d.primaryVariableId}
										usePrimaryHeight={true}
										/>
									</Provider>
								</g>
							);
						} else {
							const transformTP = `translate(0,${this.props.rootStore.visStore.timepointPositions.timepoint[i]})`;
							timepoints.push(
								<g key={d.globalIndex} transform={transformTP}>
									<Provider
										dataStore={this.props.rootStore.dataStore}
										visStore={this.props.rootStore.visStore}
									>
										<HeatmapTimepoint
											tooltipFunctions={this.props.tooltipFunctions}
											showContextMenuHeatmapRow={this.props.showContextMenuHeatmapRow}
											xOffset={(this.props.rootStore.visStore.sampleRectWidth - rectWidth) / 2}
											timepoint={d}
											rectWidth={rectWidth}
											heatmapScale={this.props.rootStore.visStore.heatmapScales[i]}
										/>
									</Provider>
								</g>
							);
						}
					}
					// create transitions
					if (i !== this.props.rootStore.dataStore.timepoints.length - 1) {
						const transformTR = `translate(0,${this.props.rootStore.visStore.timepointPositions.connection[i]})`;
						const firstTP = d;
						const secondTP = this.props.rootStore.dataStore.timepoints[i + 1];
						let transition;
						if (firstTP.isGrouped) {
							if (secondTP.isGrouped) {
								transition = (
									<Provider
										dataStore={this.props.rootStore.dataStore}
										visStore={this.props.rootStore.visStore}
									>
										<SankeyTransition
											index={i}
											firstGrouped={firstTP.grouped}
											secondGrouped={secondTP.grouped}
											firstPrimary={this.props.rootStore.dataStore.variableStores[
												firstTP.type
											].getById(firstTP.primaryVariableId)}
											secondPrimary={this.props.rootStore.dataStore.variableStores[
												secondTP.type
											].getById(secondTP.primaryVariableId)}
											tooltipFunctions={this.props.tooltipFunctions}
										/>
									</Provider>
								);
							} else {
								transition = (
									<Provider
										dataStore={this.props.rootStore.dataStore}
										visStore={this.props.rootStore.visStore}
									>
										<HeatmapGroupTransition
											inverse={false}
											index={firstTP.globalIndex}
											partitions={firstTP.grouped}
											nonGrouped={secondTP}
											heatmapScale={this.props.rootStore.visStore.heatmapScales[i + 1]}
											colorScale={
												this.props.rootStore.dataStore.variableStores[firstTP.type].getById(
													firstTP.primaryVariableId
												).colorScale
											}
										/>
									</Provider>
								);
							}
						} else if (secondTP.isGrouped) {
							transition = (
								<Provider
									dataStore={this.props.rootStore.dataStore}
									visStore={this.props.rootStore.visStore}
								>
									<HeatmapGroupTransition
										inverse
										index={secondTP.globalIndex}
										partitions={secondTP.grouped}
										nonGrouped={firstTP}
										heatmapScale={this.props.rootStore.visStore.heatmapScales[i]}
										colorScale={
											this.props.rootStore.dataStore.variableStores[secondTP.type].getById(
												secondTP.primaryVariableId
											).colorScale
										}
									/>
								</Provider>
							);
						} else {
							transition = (
								<Provider
									dataStore={this.props.rootStore.dataStore}
									visStore={this.props.rootStore.visStore}
								>
									<LineTransition
										index={firstTP.globalIndex}
										from={firstTP.patients}
										to={secondTP.patients}
										firstHeatmapScale={this.props.rootStore.visStore.heatmapScales[i]}
										secondHeatmapScale={this.props.rootStore.visStore.heatmapScales[i + 1]}
										secondTimepoint={secondTP}
										timeGapMapper={
											this.props.rootStore.staticMappers[this.props.rootStore.timeDistanceId]
										}
										colorScale={
											this.props.rootStore.dataStore.variableStores[secondTP.type].getById(
												secondTP.primaryVariableId
											).colorScale
										}
										tooltipFunctions={this.props.tooltipFunctions}
									/>
								</Provider>
							);
						}
						transitions.push(
							<g key={firstTP.globalIndex} transform={transformTR}>
								{transition}
							</g>
						);
					}
				});
				return [timepoints, transitions];
			}

			render() {
				return (
					<div className="blockView" ref={this.ref}>
						<div className="view" id="block-view">
							<Row style={{ marginLeft: '0' }}>
								<Button
									size="sm"
									onClick={this.handleTimeClick}
									disabled={
										this.props.uiStore.selectedTab === 'line' ||
										this.props.rootStore.dataStore.variableStores.between.currentVariables.length >
											0
									}
									key="actualTimeline"
								>
									<FontAwesome name="clock" />{' '}
									{this.props.uiStore.realTime ? 'Hide Relative Time' : 'Show Relative Time'}
								</Button>
							</Row>
							<Row>
								<PanelGroup direction="horizontal">
									<Panel
										defaultSize={5}
										minSize={5}
										className="pane-inactive timepointLabel"
										onResize={(size) => {
											const containerWidth =
												this.ref.current?.getBoundingClientRect().width || this.width;
											const actualWidth = (containerWidth - 40) * (size / 100);
											this.updatePaneWidth('labels', actualWidth);
										}}
									>
										<Provider
											dataStore={this.props.rootStore.dataStore}
											visStore={this.props.rootStore.visStore}
										>
											<TimepointLabels
												width={Math.max(50, (this.panes.labels?.width || 50) - 10)}
												padding={this.padding}
											/>
										</Provider>
									</Panel>

									<PanelResizeHandle className="resize-handle" />

									<Panel
										defaultSize={15}
										minSize={10}
										className="pane-inactive variableLabels"
										style={{ paddingTop: this.padding }}
										onResize={(size) => {
											const containerWidth =
												this.ref.current?.getBoundingClientRect().width || this.width;
											const actualWidth = (containerWidth - 40) * (size / 100);
											this.updatePaneWidth('variableLabels', actualWidth);
										}}
									>
										<VariableLabels
											highlightedVariable={this.highlightedVariable}
											width={(this.panes.variableLabels?.width || 150) - 10}
											setHighlightedVariable={this.setHighlightedVariable}
											removeHighlightedVariable={this.removeHighlightedVariable}
											/>
									</Panel>

									<PanelResizeHandle className="resize-handle" />

									<Panel
										defaultSize={10}
										minSize={8}
										className="pane-inactive rowControls"
										style={{ paddingTop: this.padding }}
										onResize={(size) => {
											const containerWidth =
												this.ref.current?.getBoundingClientRect().width || this.width;
											const actualWidth = (containerWidth - 40) * (size / 100);
											this.updatePaneWidth('controls', actualWidth);
										}}
									>
										<RowOperators
											highlightedVariable={this.highlightedVariable}
											width={(this.panes.controls?.width || 100) - 10}
											setHighlightedVariable={this.setHighlightedVariable}
											removeHighlightedVariable={this.removeHighlightedVariable}
											tooltipFunctions={this.props.tooltipFunctions}
											showContextMenu={this.props.showContextMenu}
											openBinningModal={this.props.openBinningModal}
											openSaveVarModal={this.props.openSaveVarModal}
										/>
									</Panel>

									<PanelResizeHandle className="resize-handle" />

									<Panel
										defaultSize={55}
										minSize={30}
										className="pane-inactive"
										style={{ paddingTop: this.padding }}
										onResize={(size) => {
											const containerWidth =
												this.ref.current?.getBoundingClientRect().width || this.width;
											const actualWidth = (containerWidth - 40) * (size / 100);
											this.updatePaneWidth('view', actualWidth);
										}}
									>
										<div ref={this.blockView} className="scrollableX">
											<svg
												width={Math.max(
													this.props.rootStore.visStore.svgWidth || 700,
													this.props.rootStore.visStore.plotWidth || 700
												)}
												height={this.props.rootStore.visStore.svgHeight}
											>
												{this.getTimepointAndTransitions()}
											</svg>
										</div>
									</Panel>

									<PanelResizeHandle className="resize-handle" />

									<Panel
										defaultSize={15}
										minSize={10}
										className="pane-inactive"
										style={{ paddingTop: this.padding }}
										onResize={(size) => {
											const containerWidth =
												this.ref.current?.getBoundingClientRect().width || this.width;
											const actualWidth = (containerWidth - 40) * (size / 100);
											this.updatePaneWidth('legend', actualWidth);
										}}
									>
										<Legend
											highlightedVariable={this.highlightedVariable}
											setHighlightedVariable={this.setHighlightedVariable}
											removeHighlightedVariable={this.removeHighlightedVariable}
											{...this.props.tooltipFunctions}
										/>
									</Panel>
								</PanelGroup>
							</Row>
						</div>
						<form id="svgform" method="post">
							<input type="hidden" id="output_format" name="output_format" value="" />
							<input type="hidden" id="data" name="data" value="" />
						</form>
					</div>
				);
			}
		}
	)
);
export default withUICallbacks(BlockView);
